import React from 'react'

/**
 * Render WhatsApp-style formatting for message previews.
 * Supports:
 * - *bold*
 * - _italic_
 * - ~strikethrough~
 * - `monospace`
 * - Nested combinations like *_bold italic_*
 *
 * Safe implementation that returns React nodes without using innerHTML.
 */
export function renderWhatsAppFormatted(text?: string): React.ReactNode {
  if (!text || typeof text !== 'string') return text || ''

  // Early exit if no formatting markers present
  if (!/[*_~`]/.test(text)) return text

  return parseWhatsAppFormat(text)
}

interface Token {
  type: 'text' | 'bold' | 'italic' | 'strike' | 'mono'
  content: string | Token[]
}

/**
 * Parse WhatsApp formatting markers and return React nodes
 */
function parseWhatsAppFormat(text: string): React.ReactNode {
  const tokens = tokenize(text)

  // Skip React rendering when no actual formatting markers resolved.
  // Important so downstream consumers (e.g., linkify) still receive a plain string.
  if (!containsFormatting(tokens)) {
    return text
  }

  return renderTokens(tokens)
}

/**
 * Tokenize text with WhatsApp formatting markers
 */
function tokenize(text: string): Token[] {
  const tokens: Token[] = []
  let i = 0
  let textBuffer = ''

  const flushTextBuffer = () => {
    if (!textBuffer) return
    tokens.push({
      type: 'text',
      content: textBuffer,
    })
    textBuffer = ''
  }

  while (i < text.length) {
    const char = text[i]

    const delimiterType = getDelimiterType(char)
    if (delimiterType) {
      const match = extractBetween(text, i, char)
      if (match) {
        flushTextBuffer()
        tokens.push({
          type: delimiterType,
          content:
            delimiterType === 'mono' ? match.content : tokenize(match.content), // Recursive for nesting
        })
        i = match.endIndex + 1
        continue
      }
    }

    textBuffer += char
    i++
  }

  flushTextBuffer()

  return tokens
}

/**
 * Extract content between matching delimiters
 */
function extractBetween(
  text: string,
  startIndex: number,
  delimiter: string
): { content: string; endIndex: number } | null {
  if (!isStartBoundary(text, startIndex)) {
    return null
  }

  const start = startIndex + delimiter.length
  const firstContentChar = text[start]

  // Opening delimiter must have non-whitespace content following it
  if (!firstContentChar || isForbiddenBoundaryCharacter(firstContentChar)) {
    return null
  }

  let end = start

  // Find closing delimiter (must not be at start, and must have content)
  while (end < text.length) {
    if (
      text[end] === delimiter &&
      end > start &&
      !isForbiddenBoundaryCharacter(text[end - 1]) &&
      isEndBoundary(text, end, delimiter.length)
    ) {
      const content = text.substring(start, end)
      if (!content.trim()) {
        return null
      }

      return {
        content,
        endIndex: end,
      }
    }
    end++
  }

  return null // No matching closing delimiter
}

/**
 * Render tokens to React nodes
 */
function renderTokens(tokens: Token[]): React.ReactNode {
  return (
    <>
      {tokens.map((token, idx) => {
        if (token.type === 'text') {
          return <React.Fragment key={idx}>{token.content}</React.Fragment>
        }

        if (token.type === 'bold') {
          return (
            <span key={idx} style={{ fontWeight: 500 }}>
              {Array.isArray(token.content)
                ? renderTokens(token.content)
                : token.content}
            </span>
          )
        }

        if (token.type === 'italic') {
          return (
            <span key={idx} style={{ fontStyle: 'italic' }}>
              {Array.isArray(token.content)
                ? renderTokens(token.content)
                : token.content}
            </span>
          )
        }

        if (token.type === 'strike') {
          return (
            <span key={idx} style={{ textDecoration: 'line-through' }}>
              {Array.isArray(token.content)
                ? renderTokens(token.content)
                : token.content}
            </span>
          )
        }

        if (token.type === 'mono') {
          return (
            <span key={idx} style={{ fontFamily: 'monospace' }}>
              {token.content}
            </span>
          )
        }

        return null
      })}
    </>
  )
}

/**
 * Determine whether token stream contains any non-text formatting segments.
 * Used to decide if we can safely return the original string.
 */
function containsFormatting(tokens: Token[]): boolean {
  return tokens.some((token) => {
    if (token.type !== 'text') {
      return true
    }

    if (Array.isArray(token.content)) {
      return containsFormatting(token.content)
    }

    return false
  })
}

function isWordCharacter(char?: string): boolean {
  if (!char) return false
  return /[A-Za-z0-9]/.test(char)
}

function isForbiddenBoundaryCharacter(char?: string): boolean {
  if (!char) return true
  return /\s/.test(char)
}

function isStartBoundary(text: string, index: number): boolean {
  const prevChar = text[index - 1]

  if (index > 0 && isWordCharacter(prevChar)) {
    return false
  }

  return true
}

function isEndBoundary(
  text: string,
  index: number,
  delimiterLength: number
): boolean {
  const nextChar = text[index + delimiterLength]

  if (nextChar && isWordCharacter(nextChar)) {
    return false
  }

  return true
}

function getDelimiterType(char: string): Token['type'] | null {
  if (char === '*') return 'bold'
  if (char === '_') return 'italic'
  if (char === '~') return 'strike'
  if (char === '`') return 'mono'
  return null
}
